import React, { createContext, useContext, useRef, useState } from "react";
import useBookviewSearch, {
    UseBookviewSearchReturn,
} from "./use-bookview-search";
import { useAtom, useSetAtom } from "jotai";
import { BookviewSearchResultsAtom } from "utilities/atoms/bookview/bookview-search-results-atom";
import { useLocation } from "react-router";
import PublicationTypes from "constants/publication-types";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import { BookviewSearchScopeAtom } from "utilities/atoms/bookview/bookview-search-scope-atom";
import { BookviewSearchScope } from "organisms/panels/bookview-tab-panels/bookview-search/bookview-search-scope";
import { useBookviewTabPanel } from "utilities/contexts/bookview-tab-panel/use-bookview-tab-panel";
import { useMobileSecondaryNavContent } from "utilities/contexts/use-mobile-secondary-nav/use-mobile-secondary-nav-content";

interface BookviewSearchContextType extends UseBookviewSearchReturn {
    searchInputRef: React.RefObject<HTMLInputElement>;
    isSearchActive: boolean;
    setIsSearchActive: (isActive: boolean) => void;
    draftSearchText: string;
    setDraftSearchText: (draftText: string) => void;
    isDisabled: boolean;
    isSearchCollapsed: boolean;
    setIsSearchCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
    searchScope: BookviewSearchScope;
    setSearchScope: (scope: BookviewSearchScope) => void;
}

const BookviewSearchContext = createContext<
    BookviewSearchContextType | undefined
>(undefined);

export const BookviewSearchProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [draftSearchText, setDraftSearchText] = useState("");
    const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);
    const { isBookviewSearchPanelOpen: isDesktopBookviewSearchPanelOpen } =
        useBookviewTabPanel();
    const { isBookviewSearchPanelOpen: isMobileBookviewSearchPanelOpen } =
        useMobileSecondaryNavContent();
    const setSearchResults = useSetAtom(BookviewSearchResultsAtom);
    const { pathname } = useLocation();
    const { publication } = useCurrentPublication();
    const isNecChapterNine =
        publication.type === PublicationTypes.NEC &&
        pathname.includes("/chapters/9");
    const isDisabled = isNecChapterNine;
    const isQueryDisabled =
        isDisabled ||
        (!isDesktopBookviewSearchPanelOpen && !isMobileBookviewSearchPanelOpen);
    const searchHook = useBookviewSearch(isQueryDisabled);
    const [searchScope, setSearchScope] = useAtom(BookviewSearchScopeAtom);

    const clearSearch = () => {
        setDraftSearchText("");
        setSearchResults([]);
        searchHook.clearSearch();
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    return (
        <BookviewSearchContext.Provider
            value={{
                ...searchHook,
                clearSearch,
                searchInputRef,
                isSearchActive,
                setIsSearchActive,
                draftSearchText,
                setDraftSearchText,
                isDisabled,
                isSearchCollapsed,
                setIsSearchCollapsed,
                searchScope,
                setSearchScope,
            }}>
            {children}
        </BookviewSearchContext.Provider>
    );
};

export const useBookviewSearchContext = (): BookviewSearchContextType => {
    const context = useContext(BookviewSearchContext);
    if (context === undefined) {
        throw new Error(
            "useBookviewSearchContext must be used within a BookviewSearchProvider"
        );
    }
    return context;
};
